import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ChevronDown, ChevronUp, Activity } from 'lucide-react';

const PALETTE = {
  navy: '#001238',
  navyMid: '#002868',
  red: '#B22234',
  gold: '#C5A55A',
  cream: '#FAF9F6',
  emerald: '#10B981',
  sky: '#5B9BD5',
};

interface WeeklyRow {
  week_start: string;
  chargemaster_mappings_added: number;
  scenarios_added: number;
  translations_added: number;
  crosswalk_added: number;
  voice_lines_added: number;
}

// One metric per bar slot. Each carries its own intro date so we render
// "absent" (light dotted slot) for weeks before the metric existed —
// distinguishing "we hadn't built this yet" from "no production this week".
// `attribution` is an optional short upstream-vendor label (e.g. ElevenLabs
// for voice synthesis) that renders as a tiny tag next to the metric name.
interface MetricSpec {
  key: keyof Omit<WeeklyRow, 'week_start'>;
  label: string;
  short: string;
  href: string | null;
  color: string;
  introDate: string; // ISO YYYY-MM-DD
  attribution?: string;
}

const METRICS: MetricSpec[] = [
  { key: 'chargemaster_mappings_added', label: 'Pricing rows ingested',           short: 'Pricing Rows Ingested',     href: '/ingestion-dashboard',          color: PALETTE.navy,    introDate: '2025-10-06' },
  { key: 'scenarios_added',             label: 'Scenarios created',               short: 'Scenarios Created',         href: '/scenarios',                    color: PALETTE.emerald, introDate: '2025-10-06' },
  { key: 'crosswalk_added',             label: 'International codes cross-mapped', short: 'Intl Codes Cross-Mapped',  href: '/international/code-explorer',  color: PALETTE.sky,     introDate: '2026-02-09' },
  { key: 'translations_added',          label: 'Scenario translations',           short: 'Translations',              href: null,                            color: PALETTE.gold,    introDate: '2026-02-23' },
  { key: 'voice_lines_added',           label: 'Scenarios voice acted',           short: 'Voice Acted (MP3 lines)',   href: null,                            color: PALETTE.red,     introDate: '2026-04-06', attribution: 'ElevenLabs' },
];

function abbreviate(n: number | null | undefined): string {
  if (n == null || n === 0) return '0';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatWeekRange(weekStart: string): string {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

interface WeeklyCadenceStripProps {
  isDark: boolean;
}

export const WeeklyCadenceStrip: React.FC<WeeklyCadenceStripProps> = ({ isDark }) => {
  const [pulseOpen, setPulseOpen] = useState(false);

  const { data: weeks, isLoading } = useQuery({
    queryKey: ['platform-metrics-weekly'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_metrics_weekly')
        .select('week_start, chargemaster_mappings_added, scenarios_added, translations_added, crosswalk_added')
        .order('week_start', { ascending: true });
      if (error) throw error;
      return (data ?? []) as WeeklyRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Compute each metric's max-this-period for relative-scale bar heights AND
  // running cumulative for the per-row "X total" label.
  // Metrics scale independently — chargemaster_mappings is BigBig vs
  // scenarios/translations, so a single shared scale would crush the
  // smaller series to invisible nubs.
  // Cumulative totals are summed from the weekly counts (NOT from
  // platform_stats) so the displayed total stays internally consistent
  // with the bars users see — same source, same number.
  const { maxByMetric, cumulativeByMetric } = useMemo(() => {
    const max: Record<string, number> = {};
    const cum: Record<string, number> = {};
    for (const m of METRICS) { max[m.key] = 0; cum[m.key] = 0; }
    for (const w of weeks ?? []) {
      for (const m of METRICS) {
        const v = (w[m.key] as number) || 0;
        if (v > max[m.key]) max[m.key] = v;
        cum[m.key] += v;
      }
    }
    return { maxByMetric: max, cumulativeByMetric: cum };
  }, [weeks]);

  // Theme tokens — match Quality-card system.
  const t = isDark ? {
    cardBg: 'linear-gradient(180deg, rgba(2,22,60,0.92) 0%, rgba(0,14,44,0.96) 100%)',
    cardBorder: 'rgba(197,165,90,0.18)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 20px -14px rgba(0,0,0,0.6)',
    headingText: PALETTE.cream,
    bodyText: 'rgba(245,235,215,0.78)',
    fadedText: 'rgba(245,235,215,0.45)',
    divider: 'rgba(197,165,90,0.18)',
    rowHover: 'rgba(245,235,215,0.04)',
    barTrack: 'rgba(245,235,215,0.06)',
    introMarker: 'rgba(245,235,215,0.10)',
  } : {
    cardBg: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)',
    cardBorder: 'rgba(0,18,56,0.12)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 4px 14px -6px rgba(0,18,56,0.10)',
    headingText: PALETTE.navy,
    bodyText: 'rgba(0,18,56,0.78)',
    fadedText: 'rgba(0,18,56,0.45)',
    divider: 'rgba(0,18,56,0.10)',
    rowHover: 'rgba(0,18,56,0.03)',
    barTrack: 'rgba(0,18,56,0.06)',
    introMarker: 'rgba(0,18,56,0.10)',
  };

  if (isLoading) {
    return (
      <div className="rounded-lg p-4 mb-6 text-sm text-center" style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, color: t.fadedText }}>
        Loading weekly cadence…
      </div>
    );
  }

  if (!weeks || weeks.length === 0) {
    return null;
  }

  // Filter out weeks before any metric was introduced (the dead zone Dec 2025–early Feb 2026)
  const earliestIntro = METRICS.reduce((min, m) => m.introDate < min ? m.introDate : min, '9999-12-31');
  const visibleWeeks = weeks.filter(w => w.week_start >= earliestIntro);

  return (
    <div
      className="rounded-lg p-4 sm:p-5 mb-8 relative overflow-hidden"
      style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, boxShadow: t.cardShadow }}
    >
      {/* Patriotic flag-stripe accent */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${PALETTE.red} 0%, ${PALETTE.cream} 50%, ${PALETTE.navyMid} 100%)` }}
        aria-hidden
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4" style={{ color: PALETTE.gold }} />
          <h3 className="text-base font-semibold" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: t.headingText }}>
            Weekly production cadence
          </h3>
          <span className="text-[10px] uppercase tracking-wider" style={{ color: t.fadedText }}>
            {visibleWeeks.length} weeks
          </span>
        </div>
        <button
          type="button"
          onClick={() => setPulseOpen(!pulseOpen)}
          className="text-[11px] uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded transition-colors"
          style={{ color: t.bodyText, border: `1px solid ${t.divider}` }}
        >
          {pulseOpen ? 'Hide' : 'Show'} pulse data
          {pulseOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {/* Sparkline rows — one per metric. Each row shows:
            • metric name + intro date + cumulative total
            • horizontal bar series scaled to per-metric max-of-period
            • peak label on the right edge (the visual "what is the tallest bar")
          Without the peak label there's no scale anchor and a 5-row spike looks
          identical to a 50M-row spike. Cumulative is summed from the same
          weekly bars so the totals stay internally consistent. */}
      <div className="space-y-2.5">
        {METRICS.map((m) => {
          const max = maxByMetric[m.key] || 1;
          const cum = cumulativeByMetric[m.key] || 0;
          const rowContent = (
            <div className="flex items-center gap-3">
              <div className="w-32 sm:w-44 flex-shrink-0">
                <div className="text-[11px] font-semibold flex items-center gap-1.5 flex-wrap" style={{ color: m.color }}>
                  <span>{m.short}</span>
                  {m.attribution && (
                    <span
                      className="text-[8px] uppercase tracking-widest px-1 py-px rounded font-medium"
                      style={{
                        background: `${m.color}22`,
                        color: m.color,
                        border: `1px solid ${m.color}55`,
                      }}
                      title={`Upstream service: ${m.attribution}`}
                    >
                      {m.attribution}
                    </span>
                  )}
                </div>
                <div className="text-[9px] uppercase tracking-wider" style={{ color: t.fadedText }}>
                  since {new Date(m.introDate).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
                  {cum > 0 && <> · <span style={{ color: t.bodyText, fontWeight: 600 }}>{abbreviate(cum)}</span> total</>}
                </div>
              </div>
              <div className="flex-1 relative">
                {/* Peak-week scale anchor — shows what the tallest bar represents,
                    so readers can decode bar heights without hovering. */}
                {max > 0 && (
                  <div
                    className="absolute -top-3.5 right-0 text-[9px] tabular-nums font-mono"
                    style={{ color: t.fadedText }}
                    title={`Peak week: ${abbreviate(max)}`}
                  >
                    peak {abbreviate(max)}/wk
                  </div>
                )}
                <div className="flex items-end gap-[1px] h-10" style={{ background: t.barTrack, borderRadius: 3, padding: '2px 3px' }}>
                {visibleWeeks.map((w) => {
                  const v = (w[m.key] as number) || 0;
                  const beforeIntro = w.week_start < m.introDate;
                  const heightPct = beforeIntro ? 0 : Math.max(2, (v / max) * 100);
                  const tooltipText = beforeIntro
                    ? `${formatWeekRange(w.week_start)} — metric not yet tracked`
                    : `${formatWeekRange(w.week_start)} — ${abbreviate(v)} ${m.short.toLowerCase()}`;
                  return (
                    <div
                      key={w.week_start}
                      className="flex-1 min-w-[2px] rounded-sm transition-opacity"
                      style={{
                        height: `${heightPct}%`,
                        background: beforeIntro ? t.introMarker : m.color,
                        opacity: v === 0 && !beforeIntro ? 0.18 : 1,
                      }}
                      title={tooltipText}
                    />
                  );
                })}
                </div>
              </div>
            </div>
          );
          if (m.href) {
            return (
              <Link
                key={m.key}
                to={m.href}
                className="block rounded transition-colors hover:no-underline"
                style={{ textDecoration: 'none', padding: '2px 4px', margin: '-2px -4px' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.rowHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                title={`See ${m.label} detail →`}
              >
                {rowContent}
              </Link>
            );
          }
          return <div key={m.key}>{rowContent}</div>;
        })}
      </div>

      {/* Pulse panel — collapsible weekly table for the data-curious */}
      {pulseOpen && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: t.divider }}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left font-semibold pb-2" style={{ color: t.fadedText }}>Week of</th>
                  {METRICS.map(m => (
                    <th key={m.key} className="text-right font-semibold pb-2 px-2" style={{ color: m.color }}>{m.short}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleWeeks.slice().reverse().map((w) => (
                  <tr key={w.week_start} className="border-t" style={{ borderColor: t.divider }}>
                    <td className="py-1.5 font-mono tabular-nums" style={{ color: t.bodyText }}>
                      {new Date(w.week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    {METRICS.map((m) => {
                      const v = (w[m.key] as number) || 0;
                      const beforeIntro = w.week_start < m.introDate;
                      return (
                        <td key={m.key} className="py-1.5 px-2 text-right tabular-nums font-mono" style={{ color: beforeIntro ? t.fadedText : (v > 0 ? t.bodyText : t.fadedText) }}>
                          {beforeIntro ? '—' : abbreviate(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              {/* Total row — sums of the visible weeks above. Makes the
                  table self-contained: scan the column, see the total. */}
              <tfoot>
                <tr style={{ borderTop: `2px solid ${t.divider}` }}>
                  <td className="py-2 pt-3 text-[10px] uppercase tracking-wider font-semibold" style={{ color: t.fadedText }}>
                    Total
                  </td>
                  {METRICS.map((m) => (
                    <td key={m.key} className="py-2 pt-3 px-2 text-right tabular-nums font-mono font-semibold" style={{ color: m.color }}>
                      {abbreviate(cumulativeByMetric[m.key] || 0)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
